import { useEffect, useState } from 'react';

import { round2 } from 'utils/costStructurePercentage';
import {
  billabilityFromPoTotal,
  validatePayoutAmount,
} from 'utils/payoutAmount';

// A Non-Billable Repair has no system calculation to mirror (any percentage of
// a $0.00 PO Total is $0.00), so the field seeds at $0.00 and anything else is
// the Supervisor's explicit entry.
const NON_BILLABLE_PREFILL = '0.00';

export type UsePayoutAmountInput = {
  // Fresh system prefill: PO Total × resolved percentage as a two-decimal
  // string, '' while unavailable (see calculateCostStructurePayout).
  calculatedCost: string;
  // Opaque key for "the payout context changed" (contractor/formula
  // selection). Any change discards a manual edit and restores the fresh
  // prefill, so a price negotiated for one contractor never carries over.
  resetKey: string;
  // The repair's PO Total; Postgres numerics arrive as strings.
  poTotal: number | string | null | undefined;
  // Whether the Payout Amount field applies at all: the calculated
  // (Percentage) payout is showing. False for hourly, Extra Hours and manual
  // Fixed Amount contractors, which this hook must never touch.
  payoutApplies: boolean;
  // The resolved Allow Payout Amount Editing toggle. It governs billable
  // repairs only — non-billability unlocks the field on its own. The result's
  // `editable` has the final say.
  editingAllowed: boolean;
};

export type UsePayoutAmountResult = {
  // A missing or non-numeric PO Total is "data not loaded" and stays
  // read-only. Dialogs branch on this when deciding what to render and submit.
  editable: boolean;
  value: string;
  setValue: (value: string) => void;
  // The system value shows in read-only regions so a stale mirror can never
  // reach the screen.
  displayValue: string;
  error: string | null;
  // The payout contribution to the RepairPayment payload. calculateCost is
  // normalized to two decimals — the precision the validator reasons in and
  // the API persists, numeric(10,2). fixedAmount travels exactly when the
  // field edits, because its presence means "Supervisor-approved amount" and
  // the API requires it then. Meaningful only with a null error.
  payment: { calculateCost: string; fixedAmount?: number };
};

// The Payout Amount behavior shared by both Add Contractor dialogs
// (master-sheet-v2 and job-repair-sheet): prefill mirroring with
// reset-on-recompute, payout validation, and the payload contribution.
// Keeping it in one place keeps the two dialogs behaviorally identical.
export const usePayoutAmount = ({
  calculatedCost,
  resetKey,
  poTotal,
  payoutApplies,
  editingAllowed,
}: UsePayoutAmountInput): UsePayoutAmountResult => {
  const [value, setValue] = useState('');

  const billability = billabilityFromPoTotal(poTotal);
  const nonBillable = billability === 'nonBillable';
  const editable =
    payoutApplies &&
    (nonBillable || (editingAllowed && billability === 'billable'));

  const prefill = nonBillable ? NON_BILLABLE_PREFILL : calculatedCost;

  useEffect(() => {
    setValue(prefill);
  }, [prefill, resetKey]);

  // Only the editable field is validated; the read-only system value is never
  // flagged. The validator detects non-billability from the PO Total itself
  // and caps entries at the Non-Billable Limit instead of the (zero) PO Total.
  const error = editable ? validatePayoutAmount({ value, poTotal }) : null;

  const submitAmount = round2(Number(value));

  return {
    editable,
    value,
    setValue,
    displayValue: editable ? value : calculatedCost,
    error,
    payment: editable
      ? { calculateCost: submitAmount.toFixed(2), fixedAmount: submitAmount }
      : { calculateCost: calculatedCost },
  };
};
